import type { AudioEngine } from './AudioEngine';

export class SoundEffects {
  private engine: AudioEngine;

  constructor(engine: AudioEngine) {
    this.engine = engine;
  }

  private get ctx(): AudioContext | null {
    return this.engine.context;
  }

  private get bus(): GainNode | null {
    return this.engine.sfxBus;
  }

  /** Soft UI click blip */
  click(): void {
    this.playTone(880, 0.05, 'sine', 0.15);
  }

  /** Resonant correction chime — bandpass sweep */
  correct(): void {
    if (!this.ctx || !this.bus) return;
    const now = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, now);
    osc.frequency.exponentialRampToValueAtTime(1200, now + 0.15);

    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(800, now);
    filter.frequency.exponentialRampToValueAtTime(1600, now + 0.15);
    filter.Q.value = 5;

    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.3);

    osc.connect(filter).connect(gain).connect(this.bus);
    osc.start(now);
    osc.stop(now + 0.3);
  }

  /** Bright ascending arpeggio — approval */
  approve(): void {
    if (!this.ctx || !this.bus) return;
    const now = this.ctx.currentTime;
    const notes = [523, 659, 784]; // C5, E5, G5

    notes.forEach((freq, i) => {
      const osc = this.ctx!.createOscillator();
      const gain = this.ctx!.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + i * 0.08);
      gain.gain.linearRampToValueAtTime(0.2, now + i * 0.08 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + 0.25);
      osc.connect(gain).connect(this.bus!);
      osc.start(now + i * 0.08);
      osc.stop(now + i * 0.08 + 0.25);
    });
  }

  /** Staccato minor buzz — rejection */
  reject(): void {
    if (!this.ctx || !this.bus) return;
    const now = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    const osc2 = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sawtooth';
    osc.frequency.value = 220;
    osc2.type = 'sawtooth';
    osc2.frequency.value = 233; // slight detune for dissonance

    gain.gain.setValueAtTime(0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);

    osc.connect(gain);
    osc2.connect(gain);
    gain.connect(this.bus);
    osc.start(now);
    osc2.start(now);
    osc.stop(now + 0.15);
    osc2.stop(now + 0.15);
  }

  /** Pulsing dissonant alarm — high drift warning */
  alarm(): void {
    if (!this.ctx || !this.bus) return;
    const now = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const lfo = this.ctx.createOscillator();
    const lfoGain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.value = 440;

    lfo.type = 'sine';
    lfo.frequency.value = 8; // 8Hz pulse
    lfoGain.gain.value = 0.15;

    lfo.connect(lfoGain);
    lfoGain.connect(gain.gain);

    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);

    osc.connect(gain).connect(this.bus);
    lfo.start(now);
    osc.start(now);
    osc.stop(now + 0.5);
    lfo.stop(now + 0.5);
  }

  /** Rising harmonic whoosh — agent spawn */
  spawn(): void {
    if (!this.ctx || !this.bus) return;
    const now = this.ctx.currentTime;

    for (let i = 0; i < 3; i++) {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(200 * (i + 1), now);
      osc.frequency.exponentialRampToValueAtTime(800 * (i + 1), now + 0.4);
      gain.gain.setValueAtTime(0.12 / (i + 1), now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
      osc.connect(gain).connect(this.bus);
      osc.start(now);
      osc.stop(now + 0.5);
    }
  }

  /** Noise burst + sub-bass — agent crash */
  crash(): void {
    if (!this.ctx || !this.bus) return;
    const now = this.ctx.currentTime;

    // Noise burst
    const bufferSize = this.ctx.sampleRate * 0.5;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (this.ctx.sampleRate * 0.1));
    }

    const noise = this.ctx.createBufferSource();
    noise.buffer = buffer;

    const noiseGain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2000, now);
    filter.frequency.exponentialRampToValueAtTime(200, now + 0.4);

    noiseGain.gain.setValueAtTime(0.4, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);

    noise.connect(filter).connect(noiseGain).connect(this.bus);
    noise.start(now);

    // Sub-bass rumble
    const sub = this.ctx.createOscillator();
    const subGain = this.ctx.createGain();
    sub.type = 'sine';
    sub.frequency.value = 50;
    subGain.gain.setValueAtTime(0.3, now);
    subGain.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
    sub.connect(subGain).connect(this.bus);
    sub.start(now);
    sub.stop(now + 0.6);
  }

  /** Descending sad tone — task expired */
  expire(): void {
    this.playTone(440, 0.2, 'triangle', 0.15, 220);
  }

  /** Ascending chime cascade — combo milestone */
  comboMilestone(level: number): void {
    if (!this.ctx || !this.bus) return;
    const now = this.ctx.currentTime;
    const baseFreq = 523 + level * 50;

    for (let i = 0; i < Math.min(level, 5); i++) {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = baseFreq * (1 + i * 0.25);
      gain.gain.setValueAtTime(0, now + i * 0.06);
      gain.gain.linearRampToValueAtTime(0.15, now + i * 0.06 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.06 + 0.3);
      osc.connect(gain).connect(this.bus);
      osc.start(now + i * 0.06);
      osc.stop(now + i * 0.06 + 0.3);
    }
  }

  /** Descending tone — despawn */
  despawn(): void {
    this.playTone(600, 0.2, 'sine', 0.15, 300);
  }

  /** Helper: play a simple tone with optional frequency ramp */
  private playTone(
    freq: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    endFreq?: number
  ): void {
    if (!this.ctx || !this.bus) return;
    const now = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(freq, now);
    if (endFreq) {
      osc.frequency.exponentialRampToValueAtTime(endFreq, now + duration);
    }

    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

    osc.connect(gain).connect(this.bus);
    osc.start(now);
    osc.stop(now + duration + 0.05);
  }
}
